import { DonationApi } from '@/services/donations/api'

// Blackbaud Luminate Online Checkout SDK. The SDK injects `window.BlackbaudCheckout`
// and dispatches a `bbcheckout-loaded` event on `document` when ready.
// This URL is the `pcoJavascriptLocation` returned by Luminate's `getDonationFormInfo` API.
export const CHECKOUT_SCRIPT_URL = 'https://pay.blackbaud.com/scripts/checkout'

let scriptLoadPromise = null
let activeCheckoutService = null

/** Parse donation amount to a finite number in dollars (for Luminate donateCheckout `other_amount`). */
export function parseCheckoutAmount(amount) {
  if (amount == null || amount === '') return NaN
  if (typeof amount === 'number') return Number.isFinite(amount) ? amount : NaN
  const cleaned = String(amount).replace(/[^0-9.]/g, '')
  if (!cleaned) return NaN
  const n = parseFloat(cleaned)
  return Number.isFinite(n) ? n : NaN
}

export function loadCheckoutScript() {
  if (scriptLoadPromise) return scriptLoadPromise

  scriptLoadPromise = new Promise((resolve, reject) => {
    if (typeof window.BlackbaudCheckout === 'function') {
      return resolve()
    }

    const existing = document.querySelector(`script[src="${CHECKOUT_SCRIPT_URL}"]`)
    const onLoaded = () => {
      document.removeEventListener('bbcheckout-loaded', onLoaded)
      resolve()
    }
    document.addEventListener('bbcheckout-loaded', onLoaded)

    if (existing) {
      // Script tag already present; the event might have already fired before we attached the listener.
      if (typeof window.BlackbaudCheckout === 'function') {
        document.removeEventListener('bbcheckout-loaded', onLoaded)
        resolve()
      }
      return
    }

    const script = document.createElement('script')
    script.src = CHECKOUT_SCRIPT_URL
    script.async = true
    script.onerror = () => {
      document.removeEventListener('bbcheckout-loaded', onLoaded)
      scriptLoadPromise = null
      reject(new Error('Failed to load Blackbaud Checkout SDK'))
    }
    document.head.appendChild(script)
  })

  return scriptLoadPromise
}

export function extractTransactionToken(event, checkoutService, latestTransactionId) {
  const candidates = [
    event?.transactionToken,
    event?.transaction_token,
    event?.token,
    event?.TokenId,
    event?.id,
    event?.transactionId,
    event?.transaction_id,
    // Blackbaud Checkout's `checkoutComplete` payload wraps the token in a
    // `transaction` object: `{ success: true, transaction: { id, ... } }`.
    event?.transaction?.id,
    event?.transaction?.transactionId,
    event?.transaction?.transaction_id,
    event?.transaction?.transactionToken,
    event?.transaction?.token,
    event?.details?.transactionToken,
    event?.details?.transaction_token,
    event?.details?.token,
    event?.details?.id,
    event?.details?.transactionId,
    event?.details?.transaction?.id,
    event?.details?.transaction?.transactionId,
    latestTransactionId,
    checkoutService?.transactionId,
  ]
  for (const value of candidates) {
    if (value != null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

export function extractCheckoutErrorMessage(event) {
  return (
    event?.errorMessage ||
    event?.message ||
    event?.declineUserMessage ||
    event?.details?.errorMessage ||
    event?.details?.message ||
    event?.details?.declineUserMessage ||
    ''
  )
}

/** Close the active Blackbaud Checkout modal if one is open. */
export function closeActiveCheckoutModal() {
  const service = activeCheckoutService
  if (!service) return
  try {
    service.checkoutModal?.close?.()
  } catch (_) {
    // ignore close errors
  }
  activeCheckoutService = null
}

/**
 * Opens the Blackbaud Luminate Online Checkout modal and resolves with the transaction token.
 *
 * @param {Object} params
 * @param {string} params.environmentId          - Luminate Online environmentId (from getDonationFormInfo or BB admin envid).
 * @param {string} params.paymentConfigurationId - Luminate Online paymentConfigurationId (a.k.a. merchantAccountConfigurationId).
 * @param {number|string} params.amount          - Donation amount in dollars.
 * @param {string} params.firstName              - Billing first name.
 * @param {string} params.lastName               - Billing last name.
 * @param {string} params.street1                - Billing street address.
 * @param {string} params.city                   - Billing city.
 * @param {string} params.state                  - Billing state.
 * @param {string} params.zip                    - Billing zip / postal code.
 * @param {string} params.email                  - Donor email.
 * @returns {Promise<string>} The Blackbaud Checkout transaction token.
 */
export async function openCheckoutModal({
  environmentId,
  paymentConfigurationId,
  amount,
  firstName,
  lastName,
  street1,
  city,
  state,
  zip,
  email,
}) {
  if (!paymentConfigurationId) {
    throw new Error(
      'Blackbaud Checkout is not configured: missing paymentConfigurationId on the payment provider.',
    )
  }

  const amountDollars = parseCheckoutAmount(amount)
  if (!Number.isFinite(amountDollars) || amountDollars <= 0) {
    throw new Error('Please enter a valid donation amount before continuing to payment.')
  }

  // Checkout SDK expects baseAmount in cents (minimum 50 cents per Blackbaud).
  const baseAmountCents = Math.round(amountDollars * 100)
  if (baseAmountCents < 50) {
    throw new Error('Minimum donation amount is $0.50.')
  }

  await loadCheckoutScript()

  if (typeof window.BlackbaudCheckout !== 'function') {
    throw new Error('Blackbaud Checkout SDK loaded but window.BlackbaudCheckout is unavailable.')
  }

  return new Promise((resolve, reject) => {
    const checkoutConfiguration = {
      paymentConfigurationId,
      environmentId,
      applicationName: 'Luminate',
      workflowMode: 'modal',
      languageLocale: 'en_US',
      feeOffsetType: 0,
      paymentMethodOptions: {
        wallets: {
          applePayEnabled: false,
          googlePayEnabled: false,
        },
      },
    }

    let settled = false
    let latestTransactionId = ''
    let checkoutService
    try {
      checkoutService = window.BlackbaudCheckout(checkoutConfiguration)
      activeCheckoutService = checkoutService
    } catch (e) {
      return reject(e)
    }

    const txSub = checkoutService.transactionIdChanges?.subscribe?.((id) => {
      if (id != null && String(id).trim() !== '') {
        latestTransactionId = String(id).trim()
      }
    })

    const completeSub = checkoutService.checkoutComplete?.subscribe?.((event) => {
      if (settled) return
      try {
        console.log('[BBMSCheckout] checkoutComplete event:', JSON.parse(JSON.stringify(event)))
      } catch (_) {
        console.log('[BBMSCheckout] checkoutComplete event (raw):', event)
      }
      console.log(
        '[BBMSCheckout] checkoutService.transactionId =',
        checkoutService?.transactionId,
        'latestTransactionId =',
        latestTransactionId,
      )

      const token = extractTransactionToken(event, checkoutService, latestTransactionId)
      const looksSuccessful = event?.success !== false

      if (looksSuccessful && token) {
        settled = true
        cleanup()
        closeActiveCheckoutModal()
        return resolve(token)
      }

      if (looksSuccessful && !token) {
        // Some Blackbaud environments fire `checkoutComplete` before the
        // `transaction-id-change` postMessage. Wait briefly for the id.
        setTimeout(() => {
          if (settled) return
          const lateToken = extractTransactionToken(event, checkoutService, latestTransactionId)
          settled = true
          cleanup()
          closeActiveCheckoutModal()
          if (lateToken) return resolve(lateToken)
          reject(
            new Error(
              extractCheckoutErrorMessage(event) ||
                'Checkout completed without a transaction token.',
            ),
          )
        }, 750)
        return
      }

      settled = true
      cleanup()
      closeActiveCheckoutModal()
      reject(
        new Error(
          extractCheckoutErrorMessage(event) || 'Payment was declined or could not be processed.',
        ),
      )
    })

    const cancelSub = checkoutService.checkoutModal?.canceled?.subscribe?.(() => {
      if (settled) return
      settled = true
      cleanup()
      closeActiveCheckoutModal()
      reject(new Error('Payment was cancelled by the donor.'))
    })

    const handleCheckoutMessage = (event) => {
      if (settled || !event?.data?.messageType) return
      const messageType = event.data.messageType
      if (messageType === 'bbcheckout-transaction-id-change') {
        const details = event.data.details || {}
        if (details.id) {
          latestTransactionId = String(details.id).trim()
          console.log('[BBMSCheckout] transaction-id-change message:', details)
        }
        return
      }
      if (messageType !== 'bbcheckout-checkout-complete') return
      const details = event.data.details || {}
      console.log('[BBMSCheckout] checkout-complete postMessage details:', details)
      if (details.success === false) {
        settled = true
        cleanup()
        closeActiveCheckoutModal()
        reject(
          new Error(
            extractCheckoutErrorMessage(details) ||
              'Payment was declined or could not be processed.',
          ),
        )
        return
      }
      // Fallback resolver: some Blackbaud environments deliver the token only
      // via the postMessage payload and never via the checkoutComplete
      // Observable, so resolve here too if we can extract a token.
      const postMessageToken = extractTransactionToken(
        details,
        checkoutService,
        latestTransactionId,
      )
      if (postMessageToken) {
        settled = true
        cleanup()
        closeActiveCheckoutModal()
        resolve(postMessageToken)
      }
    }
    window.addEventListener('message', handleCheckoutMessage)

    function cleanup() {
      window.removeEventListener('message', handleCheckoutMessage)
      try {
        txSub?.unsubscribe?.()
        completeSub?.unsubscribe?.()
        cancelSub?.unsubscribe?.()
      } catch (_) {
        // ignore unsubscribe errors
      }
      if (activeCheckoutService === checkoutService) {
        activeCheckoutService = null
      }
    }

    // Blackbaud Checkout modal requires `baseAmount` in cents, not dollars.
    const openPayload = {
      baseAmount: baseAmountCents,
      billingAddress: {
        firstName,
        lastName,
        addressLine1: street1,
        city,
        state,
        postalCode: zip,
        country: 'US',
        email,
      },
      cardholderName: `${firstName} ${lastName}`.trim(),
    }

    try {
      if (typeof checkoutService.baseAmount !== 'undefined') {
        checkoutService.baseAmount = baseAmountCents
      }

      const modal = checkoutService.checkoutModal
      if (modal && typeof modal.open === 'function') {
        modal.open(openPayload)
      } else if (typeof checkoutService.open === 'function') {
        checkoutService.open(openPayload)
      } else {
        cleanup()
        reject(new Error('Blackbaud Checkout SDK does not expose checkoutModal.open().'))
      }
    } catch (e) {
      cleanup()
      reject(e)
    }
  })
}

/**
 * Submits a donateCheckout request via our Django backend, which proxies it
 * to Luminate Online's CRDonationAPI server-to-server.
 *
 * We can't call Luminate directly from the browser because CRDonationAPI does
 * not send CORS headers (returns 403 on the preflight). The Django endpoint
 * looks up the campaign's PaymentProvider.custom_domain so we never trust the
 * URL coming from the client.
 *
 * @param {Object} options
 * @param {string} options.campaign    - Campaign id (used by Django to resolve the customDomain).
 * @param {string} [options.formToken] - Signed donor form grant, for anonymous QR devices.
 * @param {Object} options.params      - donateCheckout parameters (including `transaction_token`).
 * @returns {Promise<Object>} donationResponse from Blackbaud
 */
export async function submitDonateCheckout({ campaign, formToken, params }) {
  const data = await DonationApi.csc.submitBBMSCheckout({
    campaign,
    formToken,
    ...params,
  })

  if (data?.errorResponse) {
    throw new Error(data.errorResponse.message || 'donateCheckout failed')
  }
  if (data?.donationResponse?.errors) {
    const err = data.donationResponse.errors
    throw new Error(err.message || `donateCheckout error (code ${err.code})`)
  }
  return data?.donationResponse
}

/** Normalize Luminate donateCheckout donation block (camelCase or snake_case). */
export function parseDonationResponse(donationResponse) {
  const donation = donationResponse?.donation ?? {}
  return {
    transactionId: donation.transactionId ?? donation.transaction_id ?? '',
    confirmationCode: donation.confirmationCode ?? donation.confirmation_code ?? '',
    consId: donation.consId ?? donation.cons_id ?? '',
  }
}
